/**
 * `BackGuard`: what makes Back *ask* before it throws away something live (a
 * run, a match), instead of closing the screen. Both of the owner's games
 * built the same one (Interlock ADR 0047, TwinTen's game-screen guard); this
 * is it, once, on the shell's own history stack.
 *
 * ## Why it is an entry of its own
 *
 * A screen entered from the menu is one overlay entry (`pushOverlay`), and an
 * entry's callback may only close what it belongs to: it cannot stop, ask a
 * question and decide later. So while something is live, a second entry is
 * stacked on top of the screen's. Back pops *that* one, and all its callback
 * does is flip `asking` to true. The question itself is the game's
 * `ConfirmDialog`, rendered from `asking`, which pushes an entry of its own.
 *
 * ## The one rule
 *
 * The guard's entry exists exactly while `live && !asking`:
 *
 * | what happens                          | asking | the guard's entry      |
 * | ------------------------------------- | ------ | ---------------------- |
 * | it becomes live (`setLive(true)`)     | false  | pushed                 |
 * | Back                                  | true   | popped by the browser  |
 * | the on-screen Quit button (`request`) | true   | released (walked back) |
 * | the safe answer (`keep`)              | false  | pushed again           |
 * | it stops being live                   | false  | released               |
 * | the screen goes away (`dispose`)      | —      | released               |
 *
 * Every release goes through the function `pushOverlay` returned, a no-op
 * when a real Back already consumed the entry, which keeps the browser's
 * depth and the stack in step on every path above.
 *
 * The question's own answer is the game's: on "Leave" it calls `dispose()`
 * and navigates; on "Keep playing" (and on the dialog's Escape, Back or
 * backdrop, which `ConfirmDialog` routes to its cancel) it calls `keep()`.
 *
 * Plain TypeScript with the push function injectable, so the table is tested
 * against the real stack in jsdom and a component only mirrors `asking` into
 * a `$state`:
 *
 * ```ts
 * let asking = $state(false);
 * const guard = new BackGuard((next) => (asking = next));
 * onMount(() => { guard.setLive(true); return () => guard.dispose(); });
 * ```
 *
 * (A real-phone caveat Interlock's ADR records: Chromium may skip, on Back,
 * an entry that was pushed without a user gesture. `keep()` is normally
 * called from a tap, so the re-armed entry has one.)
 */
import { pushOverlay } from './historyStack.js';

/** `pushOverlay`'s shape: register a close callback, get back the function that releases the entry. */
export type PushOverlay = (onClose: () => void) => () => void;

export class BackGuard {
  private live = false;
  private askingValue = false;
  private release: (() => void) | null = null;
  private disposed = false;
  private readonly onAskingChange: (asking: boolean) => void;
  private readonly push: PushOverlay;

  /**
   * @param onAskingChange told every time `asking` changes; a component
   *   mirrors it into the `$state` its `ConfirmDialog` is rendered from
   * @param push the history stack to arm on; the shell's `pushOverlay` by default
   */
  constructor(onAskingChange: (asking: boolean) => void, push: PushOverlay = pushOverlay) {
    this.onAskingChange = onAskingChange;
    this.push = push;
  }

  /** True while the question is on screen. */
  get asking(): boolean {
    return this.askingValue;
  }

  /** True while Back would ask rather than leave. */
  get armed(): boolean {
    return this.release !== null;
  }

  /**
   * Whether there is something to lose right now. Going from live to not
   * live withdraws the question too: a match the server just ended has
   * nothing left to ask about.
   */
  setLive(live: boolean): void {
    if (this.disposed || this.live === live) return;
    this.live = live;
    if (!live) this.setAsking(false);
    this.sync();
  }

  /**
   * The on-screen Quit / Leave button. Returns `true` when the question was
   * put (or is already up), `false` when there is nothing to ask about and
   * the caller should simply leave.
   */
  request(): boolean {
    if (this.disposed || !this.live) return false;
    this.setAsking(true);
    this.sync();
    return true;
  }

  /** The safe answer: "Keep playing", Escape, Back, the backdrop. Re-arms the guard. */
  keep(): void {
    if (this.disposed) return;
    this.setAsking(false);
    this.sync();
  }

  /** The screen is going away, however it came to: give the entry back. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.live = false;
    this.askingValue = false;
    this.sync();
  }

  private setAsking(next: boolean): void {
    if (this.askingValue === next) return;
    this.askingValue = next;
    this.onAskingChange(next);
  }

  private sync(): void {
    const want = this.live && !this.askingValue;
    if (want && this.release === null) {
      this.release = this.push(() => {
        // A real Back popped the entry: it is gone, there is nothing to
        // release. All this does is ask.
        this.release = null;
        this.setAsking(true);
      });
    } else if (!want && this.release !== null) {
      const release = this.release;
      this.release = null;
      release();
    }
  }
}
